// =============================================================================
// domain/propReport.ts — the Prop Report's Health tab.
// -----------------------------------------------------------------------------
// "Am I safe right now?" — the compliance half of the portfolio view. Funded
// Status, Account Progress, and the daily P&L heatmap.
//
// ── 🚨 EVERY NUMBER HERE IS CLASS L (D47) ──
//
// Combined DD remaining, each account's progress bar, the profit-target
// percentage — all functions of the ENTIRE trade history. Under a bounded read
// they are not less precise, they are WRONG IN THE OPTIMISTIC DIRECTION: a
// drawdown buffer with more room than the account has, on the screen a trader
// checks before sizing up.
//
// This module cannot enforce that — `domain/` takes plain arrays and is correct
// for whatever it is handed, which is what keeps it importable into a Deno Edge
// Function. The enforcement is `selectors/propReport.ts`, which demands a
// `WholeBook`. That split is the D47 mechanism, not an accident of layering.
//
// ── ⚠ TWO DATE RULES, AND THE ASYMMETRY IS BETA'S ──
//
// `tradesInAttempt` (inside the account maths) scopes by `effectiveTradeDate`, so
// it follows the day-boundary mode. The YTD roll-up and the heatmap below bucket
// by the RAW `date`, because beta's `tradePnlForAccounts` and its heatmap both
// read `t.core.date` directly.
//
// That is inconsistent and it is preserved deliberately — `domain/account.ts`
// already carries the same note about `accountBalanceTimeline`. Changing either
// one in isolation silently moves a balance, and moving a prop account's balance
// is the one edit in this product with money attached.
// =============================================================================

import { PROP_FEE_TYPES } from './types';
import {
  computeDrawdownBuffer, computeProfitTargetProgress,
  type AccountLike, type AccountTrade,
} from './account';
import type { DayBoundaryMode } from './range';

/** What the report needs from an account, on top of the account maths' own needs. */
export interface PropAccount extends AccountLike {
  name: string;
  firm: string;
  status: string;
  accountSize: number;
  excludeFromMetrics: boolean;
  /**
   * ⚠ De-clutter only — an archived account still counts everywhere on Health.
   * It is here because ECONOMICS reads it (archive stops recurring-fee accrual),
   * and `EconomicsAccount` extends this type.
   */
  archived: boolean;
}

export type PropBucket =
  | 'funded' | 'eval' | 'personal' | 'paused' | 'passed' | 'closed' | 'blown';

/**
 * Which pile an account is in, ported from `classifyPropAccount`.
 *
 * ⚠ STATUS WINS OVER TYPE, and the order is load-bearing. A blown funded account
 * is `blown`, not `funded` — beta added the explicit `passed` bucket in June
 * because passed accounts were falling through the type check and being counted
 * as active evals, which inflated both the composition line and the forward-
 * looking sections.
 *
 * ⚠ Anything not recognised is `personal`, NOT dropped. A personal account is
 * excluded from the report's prop aggregates but still exists; silently binning
 * an unknown `accountType` would make an account vanish from a screen whose whole
 * job is "here is everything you are running".
 */
export function classifyPropAccount(a: PropAccount): PropBucket {
  const status = (a.status || 'active').toLowerCase();
  if (status === 'blown') return 'blown';
  if (status === 'closed') return 'closed';
  if (status === 'passed') return 'passed';
  if (status === 'paused') return 'paused';
  const t = (a.accountType || '').toLowerCase();
  if (t === 'sim-funded' || t === 'live-funded' || t === 'funded') return 'funded';
  if (t === 'combine') return 'eval';
  return 'personal';
}

export type PropGroups = Record<PropBucket, PropAccount[]>;

/**
 * Bucket every account.
 *
 * ⚠ `excludeFromMetrics` accounts are dropped FIRST, at the source. SIM, replay
 * and dead evals are out of every cross-account aggregate by design, and the Prop
 * Report is exactly such an aggregate. Beta filters them in the same place and
 * says so.
 */
export function groupPropAccounts(accounts: readonly PropAccount[]): PropGroups {
  const groups: PropGroups = {
    funded: [], eval: [], personal: [], paused: [], passed: [], closed: [], blown: [],
  };
  for (const a of accounts) {
    if (a.excludeFromMetrics) continue;
    groups[classifyPropAccount(a)].push(a);
  }
  return groups;
}

export interface FundedStatus {
  fundedCount: number;
  evalCount: number;
  passedCount: number;
  pausedCount: number;
  blownCount: number;
  closedCount: number;
  combinedBuyingPower: number;
  combinedDDRemaining: number;
  ytdNetOperation: number;
  ytdTradePnl: number;
  ytdPayouts: number;
  ytdFees: number;
}

/** Money-in adjustment types — beta's `PROP_REVENUE_TYPES`. */
export const PROP_REVENUE_TYPES: readonly string[] = ['Payout'];
/**
 * Money-out types.
 *
 * 🔴 THIS USED TO BE A SECOND, DIFFERENT LIST, AND ITS COMMENT CALLED IT
 * "beta's `PROP_FEE_TYPES`". It was not. Beta's list is three types
 * (app/index.html:14838); this one carried a fourth, `'Subscription'`, which
 * does not exist in beta at all — not in its fee list and not in its adjustment
 * type dropdown. Beta stores a recurring monthly prop fee as an `Eval fee` with
 * "monthly subscription" in the note.
 *
 * 🚨 SO ONE ROW WOULD HAVE BEEN COUNTED TWICE, TWO DIFFERENT WAYS, INSIDE ONE
 * CLIENT. `domain/types.ts` holds the real list, and `isBalanceMoving` uses it
 * to decide whether an adjustment moves the BALANCE or the P&L. A
 * `'Subscription'` row was absent from that list, so the account maths treated
 * it as balance-moving — dragging the drawdown buffer toward the floor — while
 * this file simultaneously subtracted it as a FEE on the Funded Status card.
 * That double-count is the exact failure `account.ts` documents as the reason
 * the balance/fee split exists.
 *
 * ⚠ Latent rather than live: nothing in either client writes that type today, so
 * the two screens agreed by luck. The column is free text, so the luck ends the
 * moment anybody types it.
 *
 * ▶ Re-exported from the one list rather than restated. Two constants with one
 * name and different contents is the shape of the bug, not a detail of it.
 */
export { PROP_FEE_TYPES } from './types';

/**
 * Sum adjustment MAGNITUDES for a set of accounts, within an inclusive range.
 *
 * ── 🚨 MAGNITUDE, NOT SIGNED SUM, AND THAT IS A FIXED BUG NOT A STYLE CHOICE ──
 *
 * Beta's own comment records what happened: the amount's sign is user-editable
 * and does get typed wrong, and summing SIGNED amounts lets a wrong-signed row
 * CANCEL a correct one. Four real $19 eval fees entered as −19 / +19 / −19 / +19
 * totalled **$0 of fees** on the Funded Status card instead of $76 — silently,
 * with nothing on screen looking broken.
 *
 * Per-row `Math.abs` makes the total robust to the typo rather than dependent on
 * not making it.
 */
export function adjustmentTotal(
  accounts: readonly PropAccount[],
  types: ReadonlySet<string>,
  startISO: string | null,
  endISO: string | null,
): number {
  let sum = 0;
  for (const a of accounts) {
    for (const adj of a.adjustments ?? []) {
      if (!adj || !types.has(adj.type)) continue;
      const d = adj.date ?? '';
      if (startISO && d < startISO) continue;
      if (endISO && d > endISO) continue;
      sum += Math.abs(Number(adj.amount) || 0);
    }
  }
  return sum;
}

/**
 * Trade P&L for a set of accounts in a raw-date range.
 *
 * ⚠ RAW `date`, not `effectiveTradeDate` — see the header. Beta's
 * `tradePnlForAccounts` reads `t.core.date` directly.
 */
export function tradePnlForAccounts(
  accountIds: ReadonlySet<string>,
  trades: readonly AccountTrade[],
  startISO: string | null,
  endISO: string | null,
): number {
  if (!accountIds.size) return 0;
  let sum = 0;
  for (const t of trades) {
    if (!t.accountId || !accountIds.has(t.accountId)) continue;
    const d = t.date ?? '';
    if (startISO && d < startISO) continue;
    if (endISO && d > endISO) continue;
    sum += t.netPnl ?? 0;
  }
  return sum;
}

export function fundedStatus(
  groups: PropGroups,
  trades: readonly AccountTrade[],
  ytdStart: string,
  ytdEnd: string,
  today: string,
  mode: DayBoundaryMode,
): FundedStatus {
  const funded = groups.funded;
  // Active prop accounts — funded AND eval. Personal accounts are excluded from
  // the report entirely: it is about prop firms specifically.
  const prop = [...funded, ...groups.eval];
  const propIds = new Set(prop.map((a) => a.id));

  const ytdTradePnl = tradePnlForAccounts(propIds, trades, ytdStart, ytdEnd);
  const ytdPayouts = adjustmentTotal(prop, new Set(PROP_REVENUE_TYPES), ytdStart, ytdEnd);
  const ytdFees = adjustmentTotal(prop, new Set(PROP_FEE_TYPES), ytdStart, ytdEnd);

  return {
    fundedCount: funded.length,
    evalCount: groups.eval.length,
    passedCount: groups.passed.length,
    pausedCount: groups.paused.length,
    blownCount: groups.blown.length,
    closedCount: groups.closed.length,
    combinedBuyingPower: funded.reduce((s, a) => s + (Number(a.accountSize) || 0), 0),
    // ⚠ CLAMPED AT ZERO PER ACCOUNT, then summed. An account already past its
    // floor has a NEGATIVE buffer, and letting it net against a healthy account's
    // cushion would report portfolio room that no single account can use.
    combinedDDRemaining: funded.reduce(
      (s, a) => s + Math.max(0, computeDrawdownBuffer(a, trades, today, mode)), 0,
    ),
    ytdTradePnl,
    ytdPayouts,
    ytdFees,
    ytdNetOperation: ytdTradePnl + ytdPayouts - ytdFees,
  };
}

// ---------------------------------------------------------------------------
// Account Progress
// ---------------------------------------------------------------------------

export interface ProgressBar {
  accountId: string;
  name: string;
  firm: string;
  bucket: 'funded' | 'eval';
  /** 0–100. Drives both the fill width and the marker position. */
  pct: number;
  /** `green` · `blue` · `amber` · `orange` — resolved to tokens by `ui/`. */
  tone: 'green' | 'blue' | 'amber' | 'orange';
  label: string;
}

/**
 * One bar per ACTIVE prop account — funded first, then evals.
 *
 * ⚠ The two buckets measure different things on the same bar, which is beta's
 * design and reads correctly only because the label says which: an eval fills
 * toward its PROFIT TARGET (more is better), a funded account fills with its
 * REMAINING CUSHION (more is better too, but it starts full and drains).
 *
 * ⚠ The colour thresholds run OPPOSITE ways for the same reason — an eval at 20%
 * is early, a funded account at 20% cushion is nearly dead.
 */
export interface MoneyFormats {
  /** Signed — `+$195` / `-$80`. For a RESULT. */
  signed: (n: number) => string;
  /** Unsigned — `$3,000`. For a GOAL or a LIMIT. */
  plain: (n: number) => string;
}

/**
 * ⚠ TWO FORMATTERS, and the split is a fix rather than a flourish.
 *
 * Beta runs every figure in these labels through one signed formatter, so its
 * eval bar reads `Eval · +$195 / +$3,000` — a plus sign on the PROFIT TARGET,
 * which is a goal you have not reached. This client's own `moneyTarget`
 * docstring already names the failure: "a plus sign on a goal reads as a gain
 * already made".
 *
 * So progress-so-far stays signed (it can genuinely be negative and the sign
 * carries meaning) and the target, the cushion and the max drawdown do not.
 */
export function progressBars(
  groups: PropGroups,
  trades: readonly AccountTrade[],
  today: string,
  mode: DayBoundaryMode,
  fmt: MoneyFormats,
): ProgressBar[] {
  return [...groups.funded, ...groups.eval].map((a) => {
    const bucket = classifyPropAccount(a) === 'funded' ? 'funded' as const : 'eval' as const;
    const base = { accountId: a.id, name: a.name, firm: a.firm, bucket };

    if (bucket === 'eval') {
      const target = Number(a.rules?.profitTarget) || 0;
      if (target <= 0) {
        // ⚠ NOT hidden. An eval with no profit target configured is a setup gap
        // the user can fix, and an empty bar with no explanation reads as a bug.
        return { ...base, pct: 0, tone: 'blue' as const, label: 'Eval · target unknown' };
      }
      const ptp = computeProfitTargetProgress(a, trades, today, mode);
      const pct = ptp.pct;
      return {
        ...base,
        pct,
        tone: pct >= 80 ? 'green' as const : pct >= 50 ? 'blue' as const : 'amber' as const,
        label: `Eval · ${fmt.signed(ptp.profitSoFar ?? 0)} / ${fmt.plain(target)} · ${pct.toFixed(0)}% to pass`,
      };
    }

    const ddMax = Number(a.rules?.maxDDValue) || 0;
    const ddRem = Math.max(0, computeDrawdownBuffer(a, trades, today, mode));
    const pct = ddMax > 0 ? Math.max(0, Math.min(100, (ddRem / ddMax) * 100)) : 0;
    return {
      ...base,
      pct,
      tone: pct < 30 ? 'orange' as const : pct < 60 ? 'amber' as const : 'green' as const,
      label: `Funded · DD ${fmt.plain(ddRem)} / ${fmt.plain(ddMax)} · ${pct.toFixed(0)}% cushion`,
    };
  });
}

// ---------------------------------------------------------------------------
// Daily P&L heatmap
// ---------------------------------------------------------------------------

export interface Heatmap {
  /** 30 ISO dates, oldest first. */
  dateCols: string[];
  rows: { accountId: string; name: string; cells: (number | null)[] }[];
  /** The largest absolute daily total, for the colour ramp. */
  maxAbs: number;
}

/**
 * Daily P&L per active prop account across the last 30 days.
 *
 * ⚠ `endToday` is a parameter — `domain/` has no clock, and injecting it is also
 * what makes the grid testable at a fixed date.
 *
 * ⚠ RAW `date` again, matching beta's heatmap. A trade after the session roll
 * lands on its calendar day here while the balance maths counts it on the next
 * session — inconsistent, deliberate, and noted in the header.
 *
 * ⚠ `null` and `0` are DIFFERENT cells: null is "no trades", 0 is "traded and
 * scratched". Beta colours both as the empty tone but its tooltip distinguishes
 * them, so the distinction has to survive to the UI.
 */
export function dailyPnlHeatmap(
  groups: PropGroups,
  trades: readonly AccountTrade[],
  endToday: string,
  days = 30,
): Heatmap | null {
  const accounts = [...groups.funded, ...groups.eval];
  if (!accounts.length) return null;

  const end = new Date(`${endToday}T00:00:00Z`);
  const dateCols: string[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(end.getTime() - i * 86400000);
    dateCols.push(d.toISOString().slice(0, 10));
  }
  const first = dateCols[0] ?? endToday;
  const last = dateCols[dateCols.length - 1] ?? endToday;

  const byAccount = new Map<string, Map<string, number>>();
  for (const a of accounts) byAccount.set(a.id, new Map());

  for (const t of trades) {
    const bucket = t.accountId ? byAccount.get(t.accountId) : undefined;
    if (!bucket) continue;
    const d = t.date ?? '';
    if (!d || d < first || d > last) continue;
    bucket.set(d, (bucket.get(d) ?? 0) + (t.netPnl ?? 0));
  }

  /**
   * ── ⚠ A DELIBERATE DEVIATION FROM BETA, AND THE ONLY ONE ON THIS TAB ──
   *
   * Beta updates `maxAbs` INSIDE the accumulation loop, against the running
   * total:
   *
   *   grid[a.id][d] = (grid[a.id][d] || 0) + netPnl;
   *   if (Math.abs(grid[a.id][d]) > maxAbs) maxAbs = Math.abs(grid[a.id][d]);
   *
   * so a day of +100 then −30 records 100, not its real total of 70 — the max of
   * the intermediate sums rather than of the finished ones. It also depends on
   * the order trades arrive in, which nothing guarantees.
   *
   * `maxAbs` is only the divisor for cell OPACITY. It moves no number on screen
   * and no tooltip; matching the bug would make every cell on a mixed day slightly
   * paler than it should be, for no gain. So this computes it from the FINAL
   * totals and says so, rather than deviating quietly.
   *
   * ⚠ That reasoning holds ONLY because nothing reads this value as a figure. If
   * `maxAbs` is ever surfaced — "biggest day: $X" — this becomes a parity break
   * on a number Josh reads, and it has to be re-decided rather than inherited.
   */
  let maxAbs = 0;
  for (const bucket of byAccount.values()) {
    for (const v of bucket.values()) if (Math.abs(v) > maxAbs) maxAbs = Math.abs(v);
  }

  return {
    dateCols,
    maxAbs,
    rows: accounts.map((a) => {
      const bucket = byAccount.get(a.id);
      return {
        accountId: a.id,
        name: a.name,
        cells: dateCols.map((d) => bucket?.get(d) ?? null),
      };
    }),
  };
}
